import { colors } from './colors';

/**
 * Anything commented out is a new color token. Figma color naming matches the comments.
 */

const color_tokens = {
  // background colors
  surface: {
    base: {
      /** tan-bg */
      background: colors.dark_levels[0],
      /** bg-0 */
      surface: colors.dark_levels[1],
      /** bg-3 */
      variant_surface: colors.dark_levels[7],
      /** bg-2 */
      variant_surface_2: colors.dark_levels[3],
      /** bg-1 */
      variant_surface_3: colors.dark_levels[2],
      /** bg-elevated */
      background_elevated: colors.dark_levels[2],
      /** bg-contrast - new color token */
      background_contrast: colors.white,
      skeleton: colors.dark_levels[4],
      /** primary-blue-bg */
      primary: colors.blue[500],
      /** no match - used on console */
      success: colors.green[600],
      /** green-histogram-bg - new color token */
      green_histogram: colors.green[700],
      /** primary-yellow-bg */
      warning: colors.yellow[600],
      /** primary-red-bg */
      danger: colors.red[600],
      /** red-histogram-bg - new color token */
      red_histogram: colors.red[400],
      /** bg-4 */
      neutral: colors.dark_levels[4],
      /** neutral-histogram-bg - new color token */
      neutral_histogram: colors.neutral[700],
      /** new color token */
      disabled: colors.dark_levels[1],
      backdrop: `rgba(0, 0, 0, 0.5)`,
      // States
      hover: {
        /** bg-0-hover */
        neutral: colors.dark_levels[4],
        neutral_variant: colors.dark_levels[3],
        /** primary-blue-bg-hover */
        primary: colors.blue[400],
        /** primary-red-bg-hover */
        danger: colors.red[400],
      },
      active: {
        neutral: colors.dark_levels[1],
        neutral_variant: colors.dark_levels[2],
        primary: colors.blue[500],
        danger: colors.red[500],
      },
    },
    chart: {
      primary: '#99B8F5',
      secondary: '#79B6C2',
      blue_300: colors.blue[300],
      blue_400: colors.blue[400],
      blue_500: colors.blue[500],
      blue_600: colors.blue[600],
      danger: colors.red[400],
      success: colors.green[400],
    },
    container: {
      neutral: colors.dark_levels[3],
      /** blue-badge-bg */
      primary: colors.blue[800],
      /** success-badge-bg */
      success: colors.green[800],
      /** caution-badge-bg */
      warning: colors.yellow[900],
      /** danger-badge-bg */
      danger: colors.red[800],
      highlight: colors.yellow[800],
      // States
      hover: {
        neutral: colors.dark_levels[3],
        /** blue-badge-bg-hover */
        primary: colors.blue[700],
        success: colors.green[600],
        warning: colors.yellow[800],
        danger: colors.red[700],
      },
      active: {
        neutral: colors.dark_levels[2],
        primary: colors.blue[800],
        success: colors.green[800],
        warning: colors.yellow[900],
        danger: colors.red[800],
      },
    },
  },
  // foreground colors
  on: {
    neutral: {
      /** neutral-fg-1 */
      primary_neutral: colors.neutral[100],
      /** neutral-fg-3 */
      secondary_neutral: colors.neutral[700],
      /** neutral-fg-2 */
      tertiary_neutral: colors.neutral[500],
      /** primary-fg */
      primary: colors.blue[300],
      /** danger-fg */
      danger: colors.red[300],
      warning: colors.yellow[300],
      success: colors.green[300],
      /** disabled-fg */
      disabled: colors.neutral[800],
      loading: colors.neutral[700],
    },
    code: {
      /** code-fg-boolean */
      boolean: colors.blue[300],
      /** code-fg-string */
      string: colors.green[300],
      /** code-fg-integer */
      number: colors.yellow[300],
      /** code-fg-null */
      null: colors.red[300],
    },
    hue: {
      /** on-hue-fg */
      primary: colors.white,
      success: colors.neutral[100],
      warning: colors.yellow[500],
      danger: colors.neutral[100],
      /** on-contrast-fg - new color token */
      contrast: colors.black,
    },
    hue_container: {
      /** primary-badge-fg */
      primary: colors.blue[200],
      primary_variant: colors.blue[400],
      /** neutral-fg-3 */
      neutral: colors.neutral[700],
      secondary: colors.blue[400],
      /** success-badge-fg */
      success: colors.green[200],
      /** caution-badge-fg */
      warning: colors.yellow[300],
      /** danger-badge-fg */
      danger: colors.red[200],
    },
  },
  outline: {
    /** neutral-outline-1 */
    neutral: colors.dark_levels[5],
    /** neutral-outline-2 */
    neutral_2: colors.dark_levels[9],
    /** neutral-outline-3 */
    neutral_3: colors.dark_levels[8],
    /** contrast-line */
    contrast_line: colors.white,
    /** primary-badge-outline */
    primary: colors.blue[600],
    /** success-badge-outline */
    success: colors.green[600],
    /** caution-badge-outline */
    warning: colors.yellow[800],
    /** danger-badge-outline */
    danger: colors.red[700],
    // States
    hover: {
      neutral: colors.neutral[800],
      neutral_variant: colors.neutral[800],
      /** primary-badge-outline-hover */
      container_primary: colors.blue[500],
    },
    focus: {
      /** focused-outline */
      primary: colors.blue[300],
    },
    active: {
      neutral: colors.neutral[800],
      container_primary: colors.blue[600],
    },
  },
};

const elevation = {
  /** dark-elevation-tooltip */
  '1': '0px -1px 0px 0px rgba(255, 255, 255, 0.04), 0px 0px 0px 1px rgba(255, 255, 255, 0.12), 0px 2px 4px 0px rgba(0, 0, 0, 0.32), 0px 4px 8px 0px rgba(0, 0, 0, 0.32)',
  /** dark-elevation-menu */
  '2': '0px -1px 0px 0px rgba(255, 255, 255, 0.04), 0px 0px 0px 1px rgba(255, 255, 255, 0.12), 0px 4px 8px 0px rgba(0, 0, 0, 0.32), 0px 8px 16px 0px rgba(0, 0, 0, 0.32)',
  /** dark-elevation-modal */
  '3': '0px -1px 0px 0px rgba(255, 255, 255, 0.04), 0px 0px 0px 1px rgba(255, 255, 255, 0.12), 0px 8px 16px 0px rgba(0, 0, 0, 0.32), 0px 16px 32px 0px rgba(0, 0, 0, 0.32)',
};

const shadows = {
  primary: {
    /** dark-button-primary */
    base: `0px 0.75px 0px 0px rgba(255, 255, 255, 0.24) inset, 0px 0px 0px 1px ${colors.blue[600]}, 0px 2px 4px 0px rgba(0, 0, 0, 0.16)`,
    /** used on the DatePickerInput SelectableButton */
    selected: `0px 0.75px 0px 0px rgba(255, 255, 255, 0.20) inset, 0px 0px 0px 1px ${colors.blue[100]}, 0px 2px 4px 0px rgba(0, 0, 0, 0.32)`,
    /** dark-button-primary-focused */
    focused: `0px 0.75px 0px 0px rgba(255, 255, 255, 0.24) inset, 0px 0px 0px 1px ${colors.blue[600]}, 0px 2px 4px 0px rgba(0, 0, 0, 0.16), 0px 0px 0px 2px #141412, 0px 0px 0px 4px ${colors.blue[300]}`,
  },
  danger: {
    base: `0px 0px 0px 1px rgba(0, 0, 0, 0.12), 0px 0px 0px 1px ${colors.red[600]}`,
    focused: `0px 0px 0px 1px rgba(0, 0, 0, 0.12), 0px 0px 0px 1px ${colors.red[600]}, 0px 0px 0px 2px #FFF, 0px 0px 0px 4px ${colors.red[300]}`,
  },
  neutral: {
    /** dark-button-neutral */
    base: '0px 0px 0px 1px rgba(255, 255, 255, 0.20), 0px 2px 4px 0px rgba(0, 0, 0, 0.16)',
    /** dark-button-neutral-highlight */
    highlight: `0px 2px 4px 0px rgba(0, 0, 0, 0.16), 0px 0px 0px 1px ${colors.blue[300]}`,
    /** dark-button-neutral-focused */
    focused: `0px 0px 0px 1px rgba(255, 255, 255, 0.20), 0px 2px 4px 0px rgba(0, 0, 0, 0.16), 0px 0px 0px 2px #141412, 0px 0px 0px 4px ${colors.blue[300]}`,
  },
  input: {
    /** dark-input-neutral */
    neutral: '0px 0px 0px 1px rgba(255, 255, 255, 0.20), 0px 2px 4px 0px rgba(0, 0, 0, 0.24) inset',
    /** dark-input-neutral-focused */
    focused: `0px 0px 0px 1px rgba(255, 255, 255, 0.20), 0px 2px 4px 0px rgba(0, 0, 0, 0.24) inset, 0px 0px 0px 2px #141412, 0px 0px 0px 4px ${colors.blue[300]}`,
    /** dark-input-error */
    error: `0px 0px 0px 1px ${colors.red[500]}, 0px 1px 2px 0px rgba(0, 0, 0, 0.16) inset`,
  },
};

export default {
  colors: color_tokens,
  elevation,
  shadows,
};
